// src/context/UserPlanContext.tsx
import { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "./AuthContext";
import { db } from "../firebase/firebase";
import { doc, onSnapshot } from "firebase/firestore";

interface UserPlanContextType {
  isPro: boolean;
  loading: boolean;
}

const UserPlanContext = createContext<UserPlanContextType>({
  isPro: false,
  loading: true,
});

export const UserPlanProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { user } = useAuth();
  const [isPro, setIsPro] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // If there's no user, we know they are not on Pro
    if (!user) {
      setIsPro(false);
      setLoading(false);
      return;
    }

    const userDocRef = doc(db, `users/${user.uid}`);
    const unsubscribe = onSnapshot(
      userDocRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          // Look at `subscriptionStatus` on the root user doc
          const status = data.subscriptionStatus as string | undefined;

          setIsPro(status === "pro");
        } else {
          setIsPro(false);
        }
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching user subscriptionStatus:", error);
        setIsPro(false);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user]);

  return (
    <UserPlanContext.Provider value={{ isPro, loading }}>
      {children}
    </UserPlanContext.Provider>
  );
};

export const useUserPlan = () => useContext(UserPlanContext);
